import {
  Card,
  Col,
  Row,
  Form,
  FormListFieldData,
  Switch,
  Tooltip,
  Checkbox,
  Collapse,
  Select,
  Input,
  InputNumber,
  Popconfirm,
  Space,
  Button,
  Tabs,
  TabsProps,
  Tag,
} from "antd"
import Styles from "./styles.module.css"
import { NotificationNameDisplay } from "../NotificationNameDisplay"
import { CopyOutlined, DeleteOutlined, QuestionCircleOutlined } from "@ant-design/icons"
import { AnimationEditor } from "../../../animationEditor"
import { getCountryNameById } from "utils/countryCodes"
import CodeEditor from "@uiw/react-textarea-code-editor"
import { SourceImagesEditor } from "../../../sourceImagesEditor"
// Direct paths, not the "components" barrel: it re-exports through App.tsx and the api client,
// which makes this component unrenderable in a test.
import { PixelsEditorV2 } from "components/PixelsEditorV2"
import { UploadSoundComponent } from "components/upload/uploadSound"
import { gameLabel, isTimeBasedGameId } from "domains/specialEvents/timeBasedGames"

type Props = {
  key: string;
  restFields: FormListFieldData;
  notificationIndex: number;
  parentIndex: number;
  contestMode?: boolean;
  remove: (index: number) => void;
  duplicate: (index: number) => void;
  hideHeading?: boolean;
  codeSupported?: boolean;
};

export const NotificationItem = ({
  key,
  restFields,
  notificationIndex,
  parentIndex,
  contestMode,
  remove,
  duplicate,
  hideHeading,
  codeSupported,
}: Props) => {
  const form = Form.useFormInstance()

  const triggerEventName: string | undefined = Form.useWatch(
    ["notificationsPerGame", parentIndex, "notifications", notificationIndex, "triggerEventName"],
    form,
  )

  // No code path can pay a session campaign, so offering the field sells a reward that
  // never arrives. Revisit if the engine gains a payout for time-based games.
  const rewardable = !isTimeBasedGameId(form.getFieldValue(["notificationsPerGame", parentIndex, "gameId"]))
  const isPingEvent = triggerEventName?.toLowerCase().startsWith("ping")

  const ingameOverlayType = form.getFieldValue([
    "notificationsPerGame",
    parentIndex,
    "notifications",
    notificationIndex,
    "ingameOverlayType",
  ])
  const defaultCSS
    = ingameOverlayType === "generic"
      ? undefined
      : JSON.stringify(
        {
          width: "15%",
          minWidth: 150,
          maxWidth: 450,
        },
        null,
        2,
      )

  const enabled = Form.useWatch(
    ["notificationsPerGame", parentIndex, "notifications", notificationIndex, "enabled"],
    form,
  )

  const renderImageEditor = () => (
    <Form.List name={[notificationIndex, "sourceImagesByGeo"]}>
      {fields => {
        const items: TabsProps["items"] = fields.map(({ name: countryIndex }) => {
          const country = form.getFieldValue([
            "notificationsPerGame",
            parentIndex,
            "notifications",
            notificationIndex,
            "sourceImagesByGeo",
            countryIndex,
            "country",
          ])

          const getTooltipTitle = () => {
            if (country === "global") {
              return "Global images are used when no specific country images are available."
            }

            return getCountryNameById(country)
          }

          return {
            label: <Tooltip title={getTooltipTitle()}>{country}</Tooltip>,
            key: country,
            children: (
              <SourceImagesEditor
                key={countryIndex}
                notificationIndex={notificationIndex}
                notificationsPerGameArrayIndex={parentIndex}
                countryIndex={countryIndex}
              />
            ),
          }
        })

        return <Tabs items={items} />
      }}
    </Form.List>
  )

  const renderPixelsEditor = () => (
    <Form.Item {...restFields} name={[notificationIndex, "trackingPixels"]}>
      <PixelsEditorV2
        pixels={
          form.getFieldValue([
            "notificationsPerGame",
            parentIndex,
            "notifications",
            notificationIndex,
            "trackingPixels",
          ]) ?? []
        }
        onChange={pixels => {
          form.setFieldsValue({
            notificationsPerGame: {
              [parentIndex]:
              {
                notifications:
                { [notificationIndex]: { trackingPixels: pixels } },
              },
            },
          })
        }}
      />
    </Form.Item>
  )

  const renderClickUrlEditor = () => (
    <Form.Item
      {...restFields}
      name={[notificationIndex, "clickUrl"]}
      tooltip={[
        "When set, the in-game banner becomes clickable and opens this URL in the default browser (http/https only).",
        "A configurable hotkey opens it in locked-cursor games.",
      ].join(" ")}
      rules={[{
        validator: async(_, value) => {
          if (!value) return
          let protocol = ""
          try {
            protocol = new URL(value).protocol
          } catch {
            throw new Error("Enter a valid URL")
          }
          if (protocol !== "http:" && protocol !== "https:") {
            throw new Error("Only http/https URLs are allowed")
          }
        },
      }]}
    >
      <Input placeholder="https://example.com/offer" allowClear />
    </Form.Item>
  )

  const renderClickPixelsEditor = () => (
    <Form.Item {...restFields} name={[notificationIndex, "clickTrackingPixels"]}>
      <PixelsEditorV2
        pixels={
          form.getFieldValue([
            "notificationsPerGame",
            parentIndex,
            "notifications",
            notificationIndex,
            "clickTrackingPixels",
          ]) ?? []
        }
        onChange={pixels => {
          form.setFieldsValue({
            notificationsPerGame: {
              [parentIndex]:
              {
                notifications:
                { [notificationIndex]: { clickTrackingPixels: pixels } },
              },
            },
          })
        }}
      />
    </Form.Item>
  )

  const renderCustomStylesEditor = () => (
    <Form.Item
      label="Custom styles"
      name={[notificationIndex, "customStyles"]}
      rules={[
        {
          message: "Invalid JSON",
          validator: async(_, value) => {
            if (!value) return Promise.resolve()
            try {
              JSON.parse(value)

              return Promise.resolve()
            } catch (error) {
              return Promise.reject(new Error("Invalid JSON"))
            }
          },
        },
      ]}
      initialValue={defaultCSS}
    >
      <CodeEditor
        language="JSON"
        padding={15}
        placeholder={"{ \"position\": \"absolute\",\"left\": 0, \"maxWidth\": 300}"}
        style={{
          backgroundColor: "#f5f5f5",
          fontFamily:
            "ui-monospace,SFMono-Regular,SF Mono,Consolas,Liberation Mono,Menlo,monospace",
        }}
      />
    </Form.Item>
  )

  const renderSoundEditor = () => (
    <Form.Item
      noStyle
      shouldUpdate={(prevValues, currentValues) => {
        const prevPlaySound = prevValues?.notificationsPerGame?.[parentIndex]?.notifications?.[notificationIndex]?.playSound
        const currentPlaySound
          = currentValues?.notificationsPerGame?.[parentIndex]?.notifications?.[notificationIndex]?.playSound

        return prevPlaySound !== currentPlaySound
      }}
    >
      {({ getFieldValue }) => {
        const playSound = getFieldValue(["notificationsPerGame", parentIndex, "notifications", notificationIndex, "playSound"])

        return playSound ? (
          <Form.Item
            {...restFields}
            name={[notificationIndex, "soundFile"]}
            label="Sound File"
            tooltip="Upload a sound file to play when this moment is triggered (default is in Dynamic UI)"
          >
            <UploadSoundComponent
              soundFile={form.getFieldValue(
                ["notificationsPerGame", parentIndex, "notifications", notificationIndex, "soundFile"]) || ""}
              setSoundFile={soundFile => {
                form.setFieldsValue(
                  { notificationsPerGame: { [parentIndex]: { notifications: { [notificationIndex]: { soundFile } } } } })
              }}
              placeholder="Upload"
            />
          </Form.Item>
        ) : null
      }}
    </Form.Item>
  )

  const renderActions = () => (
    <Space>
      <Button
        icon={<CopyOutlined />}
        onClick={() => duplicate(notificationIndex)}
      >
        Duplicate
      </Button>
      <Popconfirm
        title={`Are you sure you want to delete this notification (event "${form.getFieldValue(
          [
            "notificationsPerGame",
            parentIndex,
            "notifications",
            notificationIndex,
            "triggerEventName",
          ],
        )}" for game ${
          gameLabel(form.getFieldValue(["notificationsPerGame", parentIndex, "gameId"]))
        })?`}
        onConfirm={() => remove(notificationIndex)}
        okText="Delete"
        cancelText="Cancel"
      >
        <Button danger icon={<DeleteOutlined />}>
          Delete
        </Button>
      </Popconfirm>
    </Space>
  )

  if (hideHeading) {
    return (
      <div key={key} className={Styles.compactDetails} hidden={!enabled}>
        <div className={Styles.compactSettingsGrid}>
          <Form.Item
            {...restFields}
            name={[notificationIndex, "triggerEventName"]}
            label="Event"
            tooltip="The name of the event that triggers the notification."
          >
            <Input />
          </Form.Item>
          {!contestMode && rewardable && (
            <Form.Item
              {...restFields}
              name={[notificationIndex, "buffsReward"]}
              label="Reward (Buffs)"
              tooltip="The number of buffs the player receives when the notification is triggered."
            >
              <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
            </Form.Item>
          )}
          <Form.Item
            {...restFields}
            name={[notificationIndex, "triggerDelay"]}
            label="Trigger Delay (s)"
            tooltip="The delay between the event and the notification being triggered."
          >
            <InputNumber min={0} step={0.1} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            {...restFields}
            name={[notificationIndex, "duration"]}
            label="Duration (s)"
            tooltip="The duration of the notification in seconds."
          >
            <InputNumber min={0} step={1} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            {...restFields}
            name={[notificationIndex, "spawnProbability"]}
            label="Spawn Probability"
            tooltip="The probability of the notification being triggered. 0 = never, 1 = always."
          >
            <InputNumber min={0} max={1} step={0.01} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            {...restFields}
            name={[notificationIndex, "cooldown"]}
            label="Cooldown (s)"
            tooltip="Minimum time before this notification can be triggered again."
          >
            <InputNumber min={0} step={1} placeholder="Optional" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            {...restFields}
            name={[notificationIndex, "ingameOverlayType"]}
            label="Overlay Type"
          >
            <Select style={{ width: "100%" }} defaultValue="stacking">
              <Select.Option value="stacking">Stacking</Select.Option>
              <Select.Option value="generic">Generic</Select.Option>
            </Select>
          </Form.Item>
          {!contestMode && (
            <Form.Item
              {...restFields}
              name={[notificationIndex, "ignoreSpawnCap"]}
              valuePropName="checked"
              label="Ignore Spawn Cap"
            >
              <Checkbox />
            </Form.Item>
          )}
          <Form.Item
            {...restFields}
            name={[notificationIndex, "playSound"]}
            valuePropName="checked"
            label="Play Sound"
          >
            <Checkbox />
          </Form.Item>
          {isPingEvent && (
            <Form.Item
              {...restFields}
              name={[notificationIndex, "excludedIsps"]}
              label="Excluded ISPs"
            >
              <Select mode="tags" placeholder="Type an ISP name and press Enter" style={{ width: "100%" }} />
            </Form.Item>
          )}
        </div>

        <div className={Styles.compactAssignmentGrid}>
          <div className={Styles.compactPanel}>
            <div className={Styles.compactPanelHeader}>Images</div>
            {renderImageEditor()}
          </div>
          <div className={Styles.compactPanel}>
            <div className={Styles.compactPanelHeader}>Tracking Pixels</div>
            {renderPixelsEditor()}
          </div>
          <div className={Styles.compactPanel}>
            <div className={Styles.compactPanelHeader}>Click URL (offer link)</div>
            {renderClickUrlEditor()}
          </div>
          <div className={Styles.compactPanel}>
            <div className={Styles.compactPanelHeader}>Click Tracking Pixels</div>
            {renderClickPixelsEditor()}
          </div>
        </div>

        <Collapse ghost className={Styles.compactAdvanced}>
          <Collapse.Panel header="Advanced: raw styles, sound file, animation" key="advanced">
            <div className={Styles.compactAdvancedGrid}>
              <div>
                {renderSoundEditor()}
                {renderCustomStylesEditor()}
              </div>
              <AnimationEditor
                notificationsPerGameIndex={parentIndex}
                parentIndex={notificationIndex}
              />
            </div>
          </Collapse.Panel>
        </Collapse>

        <div className={Styles.compactActions}>
          {renderActions()}
        </div>
      </div>
    )
  }


  return (
    <div key={key} className={Styles.wrapper}>
      {!hideHeading && (
        <h3 className={Styles.title}>
          <Form.Item {...restFields} name={[notificationIndex, "triggerEventName"]} noStyle>
            <NotificationNameDisplay index={notificationIndex} parentIndex={parentIndex} />
          </Form.Item>
          {codeSupported === false && <Tag color="warning">Not shipped</Tag>}
          <Form.Item
            noStyle
            {...restFields}
            name={[notificationIndex, "enabled"]}
            valuePropName="checked"
            initialValue={true}
            getValueFromEvent={(checked: boolean) => codeSupported === false ? false : checked}
          >
            <Switch />
          </Form.Item>
        </h3>
      )}
      <Row hidden={!enabled}>
        <Col span={8}>
          <Card size="small" className={Styles.fullHeightCard} title="Main">
            <Row gutter={12}>
              {!contestMode && (
                <Col span={24}>
                  <Tooltip title="If checked, the notification will ignore the game's spawn cap.">
                    <Form.Item
                      {...restFields}
                      name={[notificationIndex, "ignoreSpawnCap"]}
                      valuePropName="checked"
                      style={{ width: 150 }}
                    >
                      <Checkbox>Ignore Spawn Cap</Checkbox>
                    </Form.Item>
                  </Tooltip>
                </Col>
              )}

              <Col span={24}>
                <Form.Item
                  {...restFields}
                  name={[notificationIndex, "ingameOverlayType"]}
                  label="Ingame Overlay Type"
                  tooltip={
                    <div>
                      The type of the ingame overlay that will be used for the notification.
                      <br />
                      <br />
                      <b>Stacking:</b> Notifications will be displayed on the left side of the
                      screen, and will stack on top of each other.
                      <br />
                      <b>Generic:</b> The notification will be displayed as set in Custom styles
                      textarea below.
                    </div>
                  }
                >
                  <Select style={{ width: "100%" }} defaultValue="stacking">
                    <Select.Option value="stacking">Stacking</Select.Option>
                    <Select.Option value="generic">Generic</Select.Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  {...restFields}
                  name={[notificationIndex, "triggerEventName"]}
                  label="Event"
                  tooltip="The name of the event that triggers the notification."
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col flex="250px">
                <Form.Item
                  {...restFields}
                  name={[notificationIndex, "spawnProbability"]}
                  label="Spawn Probability (0 - 1)"
                  tooltip="The probability of the notification being triggered. 0 = never, 1 = always."
                >
                  <InputNumber min={0} max={1} step={0.01} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              {contestMode && (
                <Col flex="240px">
                  <Form.Item
                    {...restFields}
                    name={[notificationIndex, "contestPointsReward"]}
                    label="Contest points reward"
                    tooltip="The amount of contest points user will receive for seeing this notification"
                  >
                    <InputNumber min={0} step={0.1} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
              )}
              {contestMode && (
                <Col span={24}>
                  <Form.Item
                    {...restFields}
                    name={[notificationIndex, "endgameDisplayTitle"]}
                    label="Endgame Display Title"
                    tooltip="If empty, this reward won't be displayed in the endgame screen"
                  >
                    <Input />
                  </Form.Item>
                </Col>
              )}
              {!contestMode && rewardable && (
                <Col flex="180px">
                  <Form.Item
                    {...restFields}
                    name={[notificationIndex, "buffsReward"]}
                    label="Buffs Reward"
                    tooltip="The number of buffs the player receives when the notification is triggered."
                  >
                    <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
              )}
              <Col flex="210px">
                <Form.Item
                  {...restFields}
                  name={[notificationIndex, "duration"]}
                  label="Duration (seconds)"
                  tooltip="The duration of the notification in seconds."
                >
                  <InputNumber min={0} step={1} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col flex="240px">
                <Form.Item
                  {...restFields}
                  name={[notificationIndex, "triggerDelay"]}
                  label="Trigger Delay (seconds)"
                  tooltip="The delay between the event and the notification being triggered."
                >
                  <InputNumber min={0} step={0.1} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col flex="240px">
                <Form.Item
                  {...restFields}
                  name={[notificationIndex, "cooldown"]}
                  label="Cooldown (seconds)"
                  tooltip="Minimum time before this notification can be triggered again."
                >
                  <InputNumber min={0} step={1} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              {isPingEvent && (
                <Col span={24}>
                  <Form.Item
                    {...restFields}
                    name={[notificationIndex, "excludedIsps"]}
                    label="Excluded ISPs"
                    tooltip="Notifications will not show if the user's ISP name contains any of these strings (case-insensitive).
                    E.g. 'google' will exclude 'AS15169 Google LLC'."
                  >
                    <Select mode="tags" placeholder="Type an ISP name and press Enter" style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
              )}
              <Col span={24}>
                <Form.Item
                  {...restFields}
                  name={[notificationIndex, "playSound"]}
                  valuePropName="checked"
                  tooltip="Enable sound playback for this moment"
                >
                  <Checkbox>Play Sound</Checkbox>
                </Form.Item>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) => {
                    const prevPlaySound = prevValues?.notificationsPerGame?.[parentIndex]?.notifications?.[notificationIndex]?.playSound
                    const currentPlaySound
                    = currentValues?.notificationsPerGame?.[parentIndex]?.notifications?.[notificationIndex]?.playSound

                    return prevPlaySound !== currentPlaySound
                  }}
                >
                  {({ getFieldValue }) => {
                    const playSound = getFieldValue(["notificationsPerGame", parentIndex, "notifications", notificationIndex, "playSound"])

                    return playSound ? (
                      <Form.Item
                        {...restFields}
                        name={[notificationIndex, "soundFile"]}
                        label="Sound File"
                        tooltip="Upload a sound file to play when this moment is triggered (default is in Dynamic UI)"
                      >
                        <UploadSoundComponent
                          soundFile={form.getFieldValue(
                            ["notificationsPerGame", parentIndex, "notifications", notificationIndex, "soundFile"]) || ""}
                          setSoundFile={soundFile => {
                            form.setFieldsValue(
                              { notificationsPerGame: { [parentIndex]: { notifications: { [notificationIndex]: { soundFile } } } } })
                          }}
                          placeholder="Upload sound file"
                          title="Sound File"
                        />
                      </Form.Item>
                    ) : null
                  }}
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <Form.Item
                  label="Custom styles"
                  name={[notificationIndex, "customStyles"]}
                  rules={[
                    {
                      message: "Invalid JSON",
                      validator: async(_, value) => {
                        if (!value) return Promise.resolve()
                        try {
                          JSON.parse(value)

                          return Promise.resolve()
                        } catch (error) {
                          return Promise.reject(new Error("Invalid JSON"))
                        }
                      },
                    },
                  ]}
                  initialValue={defaultCSS}
                >
                  <CodeEditor
                    language="JSON"
                    padding={15}
                    placeholder={"{ \"position\": \"absolute\",\"left\": 0, \"maxWidth\": 300}"}
                    style={{
                      backgroundColor: "#f5f5f5",
                      fontFamily:
                        "ui-monospace,SFMono-Regular,SF Mono,Consolas,Liberation Mono,Menlo,monospace",
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </Col>
        <Col span={8}>
          <Card
            size="small"
            className={Styles.fullHeightCard}
            title={
              <div className={Styles.cardHeadingWithTooltip}>
                Images
                <Tooltip title="If multiple images are present, one of them will be randomly chosen each time event is triggered">
                  <QuestionCircleOutlined className={Styles.icon} />
                </Tooltip>
              </div>
            }
          >
            <Form.List name={[notificationIndex, "sourceImagesByGeo"]}>
              {fields => {
                const items: TabsProps["items"] = fields.map(({ name: countryIndex }) => {
                  const country = form.getFieldValue([
                    "notificationsPerGame",
                    parentIndex,
                    "notifications",
                    notificationIndex,
                    "sourceImagesByGeo",
                    countryIndex,
                    "country",
                  ])

                  const getTooltipTitle = () => {
                    if (country === "global") {
                      return "Global images are used when no specific country images are available."
                    }

                    return getCountryNameById(country)
                  }

                  return {
                    label: <Tooltip title={getTooltipTitle()}>{country}</Tooltip>,
                    key: country,
                    children: (
                      <SourceImagesEditor
                        key={countryIndex}
                        notificationIndex={notificationIndex}
                        notificationsPerGameArrayIndex={parentIndex}
                        countryIndex={countryIndex}
                      />
                    ),
                  }
                })

                return (
                  <div>
                    <Tabs items={items} />
                  </div>
                )
              }}
            </Form.List>
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small" className={Styles.fullHeightCard} title="Tracking Pixels">
            <Form.Item {...restFields} name={[notificationIndex, "trackingPixels"]}>
              <PixelsEditorV2
                pixels={
                  form.getFieldValue([
                    "notificationsPerGame",
                    parentIndex,
                    "notifications",
                    notificationIndex,
                    "trackingPixels",
                  ]) ?? []
                }
                onChange={pixels => {
                  form.setFieldsValue({
                    notificationsPerGame: {
                      [parentIndex]:
                      {
                        notifications:
                        { [notificationIndex]: { trackingPixels: pixels } },
                      },
                    },
                  })
                }}
              />
            </Form.Item>
          </Card>
        </Col>
      </Row>
      <Row hidden={!enabled}>
        <Col span={16}>
          <Card size="small" title="Click URL (offer link)">
            {renderClickUrlEditor()}
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small" className={Styles.fullHeightCard} title="Click Tracking Pixels">
            {renderClickPixelsEditor()}
          </Card>
        </Col>
      </Row>
      <Row hidden={!enabled}>
        <Col span={16}>
          <AnimationEditor
            notificationsPerGameIndex={parentIndex}
            parentIndex={notificationIndex}
          />
        </Col>

        <Col span={8}>
          <Card title="Actions">
            <Space>
              <Popconfirm
                title={`Are you sure you want to delete this notification (event "${form.getFieldValue(
                  [
                    "notificationsPerGame",
                    parentIndex,
                    "notifications",
                    notificationIndex,
                    "triggerEventName",
                  ],
                )}" for game ${
                  gameLabel(form.getFieldValue(["notificationsPerGame", parentIndex, "gameId"]))
                })?`}
                onConfirm={() => remove(notificationIndex)}
                okText="Delete"
                cancelText="Cancel"
              >
                <Button type="primary" danger icon={<DeleteOutlined />}>
                    Delete
                </Button>
              </Popconfirm>
              <Button
                type="primary"
                icon={<CopyOutlined />}
                onClick={() => duplicate(notificationIndex)}
              >
                  Duplicate
              </Button>
            </Space>
          </Card>
        </Col>

      </Row>
    </div>
  )
}
